import React from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { ExportOutlined } from '@ant-design/icons';
import { formatDate } from '@/utils/dataUtils';

import {
  IInbound,
  ISummaryCostByBU,
  ISummaryCostByBUProduct,
} from '@/interfaces/inbound';

interface IProps {
  data: IInbound;
  items: ISummaryCostByBUProduct[];
  buItems: ISummaryCostByBU[];
}

const ExcelExport = ({ data, items, buItems }: IProps) => {
  const handleExport = async () => {
    // Create a new workbook and add a worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Cost Calculation');
    worksheet.getColumn('A').width = 15;
    worksheet.getColumn('B').width = 15;
    worksheet.getColumn('C').width = 15;
    worksheet.getColumn('D').width = 15;
    worksheet.getColumn('E').width = 35;
    worksheet.getColumn('F').width = 15;
    worksheet.getColumn('G').width = 15;
    worksheet.getColumn('H').width = 15;
    worksheet.getColumn('I').width = 15;
    worksheet.getColumn('J').width = 15;
    worksheet.getColumn('K').width = 15;
    worksheet.getColumn('L').width = 15;
    worksheet.getColumn('M').width = 15;
    worksheet.getCell('E3').value = data?.billingNo;
    worksheet.getCell('E3').font = {
      bold: true,
      color: { argb: 'ff0000' },
    };
    worksheet.getCell('E4').value = 'DATE OF ARRIVAL';
    worksheet.getCell('F4').value = formatDate(data?.arrivalDate);
    worksheet.getCell('F4').font = {
      bold: true,
      color: { argb: 'FFA500' },
    };
    worksheet.getCell('G4').value = 'Rate in movex';
    worksheet.getCell('G4').font = {
      bold: true,
      color: { argb: '0000FF' },
    };
    worksheet.getCell('H4').value = data?.rate;
    worksheet.getCell('H4').numFmt = '#,##0.0000';
    worksheet.getCell('H4').font = {
      bold: true,
      color: { argb: '0000FF' },
    };
    worksheet.getCell('G5').value = 'USD';
    worksheet.getCell('G5').font = {
      bold: true,
      color: { argb: '0000FF' },
    };
    worksheet.getCell('H5').value = 'THB';
    worksheet.getCell('E6').value = 'COST OF GOODS';
    worksheet.getCell('G6').value = data?.costOfGood;
    worksheet.getCell('G6').numFmt = '#,##0.00';
    worksheet.getCell('G6').alignment = {
      horizontal: 'right',
      vertical: 'middle',
      wrapText: true,
    };
    worksheet.getCell('G6').font = {
      bold: true,
      color: { argb: '0000FF' },
    };
    worksheet.getCell('H6').value = { formula: 'G6*H4' };
    worksheet.getCell('H6').numFmt = '#,##0.00';
    worksheet.getCell('H6').alignment = {
      horizontal: 'right',
      vertical: 'middle',
      wrapText: true,
    };
    worksheet.getCell('E7').value = 'DUTY FEE';
    worksheet.getCell('H7').value = data?.dutyCost;
    worksheet.getCell('H7').numFmt = '#,##0.00';
    worksheet.getCell('H7').alignment = {
      horizontal: 'right',
      vertical: 'middle',
      wrapText: true,
    };
    worksheet.getCell('E8').value = 'VAT';
    worksheet.getCell('H8').value = data?.vatCost;
    worksheet.getCell('H8').numFmt = '#,##0.00';
    worksheet.getCell('H8').alignment = {
      horizontal: 'right',
      vertical: 'middle',
      wrapText: true,
    };
    worksheet.getCell('E9').value = 'FREIGHT';
    worksheet.getCell('H9').value = data?.freight;
    worksheet.getCell('H9').numFmt = '#,##0.00';
    worksheet.getCell('H9').alignment = {
      horizontal: 'right',
      vertical: 'middle',
      wrapText: true,
    };
    worksheet.getCell('E10').value = 'TOTAL';
    worksheet.getCell('H10').value = { formula: 'SUM(H6:H9)' };
    worksheet.getCell('H10').numFmt = '#,##0.00';
    worksheet.getCell('H10').alignment = {
      horizontal: 'right',
      vertical: 'middle',
      wrapText: true,
    };
    worksheet.getCell('A12').value = 'Inv No.';
    worksheet.getCell('B12').value = '#ใบขน';
    worksheet.getCell('C12').value = 'Inv Date';
    worksheet.getCell('D12').value = 'BU';
    worksheet.getCell('E12').value = 'รายการ';
    worksheet.getCell('F12').value = 'QTY';
    worksheet.getCell('G12').value = 'FOREIGN AMT';
    worksheet.getCell('H12').value = 'THB';
    worksheet.getCell('I12').value = 'DUTY FEE';
    worksheet.getCell('J12').value = 'VAT';
    worksheet.getCell('K12').value = 'FREIGHT';
    worksheet.getCell('L12').value = 'TOTAL COST';
    worksheet.getCell('M12').value = 'PER UNIT';
    const row = worksheet.getRow(12);
    row.eachCell(cell => {
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      };
      cell.font = { bold: true, color: { argb: '0000FF' }, size: 12 };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
      };
    });

    items.forEach((item: ISummaryCostByBUProduct, index: number) => {
      const row = worksheet.addRow([
        index === 0 ? data?.invNO : '',
        index === 0 ? data?.decNO : '',
        index === 0 ? formatDate(data?.invDate) : '',
        item.businessUnitName,
        item.productName,
        item.totalQty,
        item.totalCost,
        item.totalCost,
        item.totalDuty,
        item.totalVatCost,
        item.totalFreight,
        0,
        0,
      ]);

      // Get the last added row and apply formatting/formula
      const rowIndex = row.number; // Get row index in the worksheet
      worksheet.getCell(`H${rowIndex}`).value = {
        formula: `G${rowIndex} * H4`, // Example: Calculate VAT as 7% of totalAmount
        result: item.totalUnitCostTHB || 0, // Set the initial value (important for preview)
      };

      worksheet.getCell(`K${rowIndex}`).value = {
        formula: `G${rowIndex} * H9/G6`, // Example: Calculate VAT as 7% of totalAmount
        result: item.totalFreight || 0, // Set the initial value (important for preview)
      };

      worksheet.getCell(`L${rowIndex}`).value = {
        formula: `SUM(H${rowIndex}:K${rowIndex})`,
        result: item.totalCost || 0, // Set the initial value for preview
      };

      // Prevent #DIV/0! error when F is zero
      worksheet.getCell(`M${rowIndex}`).value = {
        formula: `IF(F${rowIndex}=0, 0, L${rowIndex}/F${rowIndex})`,
        result: item.costPerUnit || 0, // Set the initial value for preview
      };

      // Apply number format with comma and 2 decimal places
      worksheet.getCell(`F${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`G${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`H${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`I${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`J${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`K${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`L${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`M${rowIndex}`).numFmt = '#,##0.00';
    });

    const totalRowIndex = (items?.length || 0) + 14;

    worksheet.getRow(totalRowIndex).values = [
      '', // Label in the first column
      '',
      '',
      '',
      '',
      '',
      {
        formula: `SUM(G13:G${13 + (items?.length - 1 || 0)})`,
        result: 0,
      },
      {
        formula: `SUM(H13:H${13 + (items?.length - 1 || 0)})`,
        result: 0,
      },
      {
        formula: `SUM(I13:I${13 + (items?.length - 1 || 0)})`,
        result: 0,
      },
      {
        formula: `SUM(J13:J${13 + (items?.length - 1 || 0)})`,
        result: 0,
      },
      {
        formula: `SUM(K13:K${13 + (items?.length - 1 || 0)})`,
        result: 0,
      },
      {
        formula: ` SUM(L13:L${13 + (items?.length - 1 || 0)})`,
        result: 0,
      },
      {
        formula: `SUM(M13:M${13 + (items?.length - 1 || 0)})`,
        result: 0,
      },
    ];

    // Apply formatting (thousands separator, 2 decimal places)
    ['G', 'H', 'I', 'J', 'K', 'L', 'M'].forEach(col => {
      worksheet.getCell(`${col}${totalRowIndex}`).numFmt =
        col === 'M' ? '#,##0.00' : '#,##0.00';
    });

    // Bold style for the total row
    worksheet.getRow(totalRowIndex).font = { bold: true };

    ['G', 'H', 'I', 'J', 'K', 'L', 'M'].forEach(col => {
      const cell = worksheet.getCell(`${col}${totalRowIndex}`);
      cell.border = {
        top: { style: 'thin' }, // Thin top border
        bottom: { style: 'double' }, // Double bottom border
      };
    });
    const diffRowIndex = totalRowIndex + 1;
    worksheet.getRow(diffRowIndex).values = [
      '', // Label in the first column
      '',
      '',
      '',
      '',
      'DIFF',
      {
        formula: `G${diffRowIndex - 1}-G6`,
        result: '-',
      },
      {
        formula: `H6-H${diffRowIndex - 1}`,
        result: '-',
      },
      {
        formula: `H7-I${diffRowIndex - 1}`,
        result: '-',
      },
      {
        formula: `H8-J${diffRowIndex - 1}`,
        result: '-',
      },
      {
        formula: `H9-K${diffRowIndex - 1}`,
        result: '-',
      },
      {
        formula: `H10-L${diffRowIndex - 1}`,
        result: '-',
      },
    ];

    const buRowIndex = diffRowIndex + 4;
    worksheet.getRow(buRowIndex).values = [
      '', // Label in the first column
      'Freight',
      'Tax',
      '',
      'Amount purchase',
      'Vat (Unclain)',
      'Total',
    ];

    buItems.forEach((item: ISummaryCostByBU) => {
      const row = worksheet.addRow([
        item.businessUnitName,
        item.totalFreight,
        item.totalDuty,
        '',
        item.totalUnitCostTHB,
        item.totalVatCost,
        item.summaryCost,
      ]);

      const rowIndex = row.number; // Get row index in the worksheet
      worksheet.getCell(`B${rowIndex}`).value = {
        formula: `SUMIF($D13:$D${13 + items.length}, A${rowIndex}, $K13:$K${13 + items.length})`, // Example: Calculate VAT as 7% of totalAmount
        result: item.totalFreight || 0, // Set the initial value (important for preview)
      };
      worksheet.getCell(`C${rowIndex}`).value = {
        formula: `SUMIF($D13:$D${13 + items.length}, A${rowIndex}, $I13:$I${13 + items.length})`, // Example: Calculate VAT as 7% of totalAmount
        result: item.totalDuty || 0, // Set the initial value (important for preview)
      };
      worksheet.getCell(`E${rowIndex}`).value = {
        formula: `SUMIF($D13:$D${13 + items.length}, A${rowIndex}, $H13:$H${13 + items.length})`, // Example: Calculate VAT as 7% of totalAmount
        result: item.totalUnitCostTHB || 0, // Set the initial value (important for preview)
      };
      worksheet.getCell(`F${rowIndex}`).value = {
        formula: `SUMIF($D13:$D${13 + items.length}, A${rowIndex}, $J13:$J${13 + items.length})`, // Example: Calculate VAT as 7% of totalAmount
        result: item.totalVatCost || 0, // Set the initial value (important for preview)
      };
      worksheet.getCell(`G${rowIndex}`).value = {
        formula: `SUM(B${rowIndex}:F${rowIndex})`, // Example: Calculate VAT as 7% of totalAmount
        result: item.summaryCost || 0, // Set the initial value (important for preview)
      };

      worksheet.getCell(`A${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`B${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`E${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`F${rowIndex}`).numFmt = '#,##0.00';
      worksheet.getCell(`G${rowIndex}`).numFmt = '#,##0.00';
    });

    const buTotalIndex = buRowIndex + buItems?.length + 1;
    worksheet.getRow(buTotalIndex).values = [
      '',
      {
        formula: `SUM(B${buRowIndex + 1}:B${buRowIndex + buItems?.length})`,
        result: '-',
      },
      {
        formula: `SUM(C${buRowIndex + 1}:C${buRowIndex + buItems?.length})`,
        result: '-',
      },
      '',
      {
        formula: `SUM(E${buRowIndex + 1}:E${buRowIndex + buItems?.length})`,
        result: '-',
      },
      {
        formula: `SUM(F${buRowIndex + 1}:F${buRowIndex + buItems?.length})`,
        result: '-',
      },
      {
        formula: `SUM(G${buRowIndex + 1}:G${buRowIndex + buItems?.length})`,
        result: '-',
      },
    ];

    ['B', 'C', 'E', 'F', 'G'].forEach(col => {
      worksheet.getCell(`${col}${buTotalIndex}`).numFmt = '#,##0.00';
    });

    worksheet.getRow(buTotalIndex).font = { bold: true };

    ['B', 'C', 'D', 'E', 'F', 'G'].forEach(col => {
      const cell = worksheet.getCell(`${col}${buTotalIndex}`);
      cell.border = {
        top: { style: 'thin' }, // Thin top border
        bottom: { style: 'double' }, // Double bottom border
      };
    });

    const diffBuRowIndex = buTotalIndex + 1;
    worksheet.getRow(diffBuRowIndex).values = [
      '',
      {
        formula: `ROUND(K${diffRowIndex - 1}-B${buTotalIndex}, 2)`,
        result: '-',
      },
      {
        formula: `ROUND(I${diffRowIndex - 1}-C${buTotalIndex}, 2)`,
        result: '-',
      },
      '',
      {
        formula: `ROUND(H${diffRowIndex - 1}-E${buTotalIndex}, 2)`,
        result: '-',
      },
      {
        formula: `ROUND(J${diffRowIndex - 1}-F${buTotalIndex}, 2)`,
        result: '-',
      },
      {
        formula: `ROUND(G${buTotalIndex}-H10, 2)`,
        result: '-',
      },
    ];
    // Generate the Excel file and trigger the download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(
      blob,
      `cost-calculation-report-${data?.invNO}-${dayjs().format('YYYY-MM-DD')}.xlsx`,
    );
  };

  return (
    <div>
      <Button type="primary" ghost onClick={handleExport}>
        <ExportOutlined /> Export to Excel
      </Button>
    </div>
  );
};

export default ExcelExport;
